import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Card,
    CardContent,
    Chip,
    IconButton,
    Divider,
    CircularProgress,
    Alert,
    Avatar,
    Collapse,
    Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import HistoryIcon from '@mui/icons-material/History';

import { getProjectCloseoutDocsHistory } from '../services/closeoutDocsService';
import {
    formatChangeType,
    getChangeTypeColor,
    formatFieldName,
    formatFieldValue,
    formatRelativeTime,
    formatAbsoluteTime,
} from '../services/closeoutDocsHistoryService';

/**
 * CloseoutDocsHistory component displays the revision history of all closeout docs
 *
 * Features:
 * - Shows chronological timeline of all document and folder changes
 * - Displays who made changes and when
 * - Expandable detail sections showing field-level changes
 * - Color-coded action chips (Created, Updated, Deleted, Moved)
 * - Visual indicators for deleted items
 * - Responsive design for mobile, tablet, and desktop
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {string} props.projectId - The project ID
 * @returns {React.ReactElement} CloseoutDocsHistory component
 */
const CloseoutDocsHistory = ({ open, onClose, projectId }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedItems, setExpandedItems] = useState({});

    // Fetch history when dialog opens
    useEffect(() => {
        if (open && projectId) {
            fetchHistory();
        }
    }, [open, projectId]);

    const fetchHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const historyData = await getProjectCloseoutDocsHistory(projectId);

            // Sort by timestamp in descending order (most recent first)
            const sortedHistory = historyData.sort((a, b) => {
                return new Date(b.timestamp) - new Date(a.timestamp);
            });

            setHistory(sortedHistory);
        } catch (err) {
            setError('Failed to load document history');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setHistory([]);
        setError(null);
        setExpandedItems({});
        onClose();
    };

    const toggleExpanded = (revisionNumber) => {
        setExpandedItems((prev) => ({
            ...prev,
            [revisionNumber]: !prev[revisionNumber],
        }));
    };

    const getActionIcon = (changeType) => {
        switch (changeType) {
            case 'FOLDER_CREATED':
                return <CreateNewFolderIcon fontSize="small" />;
            case 'DOCUMENT_UPLOADED':
                return <UploadFileIcon fontSize="small" />;
            case 'FOLDER_UPDATED':
            case 'DOCUMENT_UPDATED':
            case 'FOLDER_RENAMED':
                return <EditIcon fontSize="small" />;
            case 'FOLDER_DELETED':
            case 'DOCUMENT_DELETED':
                return <DeleteOutlineIcon fontSize="small" />;
            case 'FOLDER_MOVED':
            case 'DOCUMENT_MOVED':
                return <DriveFileMoveIcon fontSize="small" />;
            default:
                return <HistoryIcon fontSize="small" />;
        }
    };

    const renderChangeDetails = (historyItem) => {
        const hasChanges =
            historyItem.changedFields && Object.keys(historyItem.changedFields).length > 0;

        if (!hasChanges) {
            return null;
        }

        const isDeleted = historyItem.changeType?.includes('DELETED');

        return (
            <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid #E2E8F0' }}>
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        color: '#4A5568',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}
                >
                    {isDeleted ? 'Deleted Information' : 'Changes'}
                </Typography>
                <Box sx={{ mt: 1 }}>
                    {Object.entries(historyItem.changedFields).map(([field, changed]) => {
                        if (!changed) return null;

                        const oldValue = historyItem.oldValues?.[field];
                        const newValue = historyItem.newValues?.[field];

                        // For deletions, only show old value (what was deleted)
                        if (isDeleted) {
                            return (
                                <Box
                                    key={field}
                                    sx={{
                                        mb: 1,
                                        p: 1,
                                        backgroundColor: '#FFF5F5',
                                        borderRadius: 1,
                                        border: '1px solid #FED7D7',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#2D3748',
                                            mb: 0.5,
                                        }}
                                    >
                                        {formatFieldName(field)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#C53030',
                                            fontWeight: 500,
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {formatFieldValue(field, oldValue)}
                                    </Typography>
                                </Box>
                            );
                        }

                        // For non-deletions, show old -> new
                        return (
                            <Box
                                key={field}
                                sx={{
                                    mb: 1,
                                    p: 1,
                                    backgroundColor: '#F7FAFC',
                                    borderRadius: 1,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#2D3748',
                                        mb: 0.5,
                                    }}
                                >
                                    {formatFieldName(field)}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#718096', display: 'block' }}
                                        >
                                            Previous
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#E53E3E',
                                                textDecoration: 'line-through',
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {formatFieldValue(field, oldValue)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#718096', display: 'block' }}
                                        >
                                            New
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#38A169',
                                                fontWeight: 500,
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {formatFieldValue(field, newValue)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    const renderHistoryItem = (historyItem) => {
        const isExpanded = expandedItems[historyItem.revisionNumber];
        const hasChanges =
            historyItem.changedFields && Object.keys(historyItem.changedFields).length > 0;
        const isDeleted = historyItem.changeType?.includes('DELETED');

        return (
            <Card
                key={historyItem.revisionNumber}
                sx={{
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid #E2E8F0',
                    opacity: isDeleted ? 0.7 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        borderColor: '#CBD5E0',
                    },
                }}
            >
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                    <Grid container spacing={2} alignItems="flex-start">
                        {/* User Avatar */}
                        <Grid size={{ xs: 12, sm: 'auto' }}>
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: '#1652F0',
                                    fontSize: '1rem',
                                }}
                            >
                                {historyItem.modifiedByUserName
                                    ? historyItem.modifiedByUserName
                                          .split(' ')
                                          .map((n) => n[0])
                                          .join('')
                                          .toUpperCase()
                                          .slice(0, 2)
                                    : '?'}
                            </Avatar>
                        </Grid>

                        {/* Main Content */}
                        <Grid size={{ xs: 12, sm: 'grow' }}>
                            <Box>
                                {/* Action Header */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1A202C',
                                        }}
                                    >
                                        {historyItem.modifiedByUserName || 'Unknown User'}
                                    </Typography>
                                    <Chip
                                        icon={getActionIcon(historyItem.changeType)}
                                        label={formatChangeType(historyItem.changeType)}
                                        size="small"
                                        color={getChangeTypeColor(historyItem.changeType)}
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                        }}
                                    />
                                </Box>

                                {/* Entity Info */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 0.5,
                                    }}
                                >
                                    {historyItem.entityType === 'FOLDER' ? (
                                        <FolderIcon
                                            sx={{
                                                fontSize: 18,
                                                color: isDeleted ? '#E53E3E' : '#718096',
                                            }}
                                        />
                                    ) : (
                                        <DescriptionIcon
                                            sx={{
                                                fontSize: 18,
                                                color: isDeleted ? '#E53E3E' : '#718096',
                                            }}
                                        />
                                    )}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: isDeleted ? '#E53E3E' : '#2D3748',
                                            fontWeight: 500,
                                            textDecoration: isDeleted ? 'line-through' : 'none',
                                        }}
                                    >
                                        {historyItem.entityName}
                                    </Typography>
                                    {isDeleted && (
                                        <Chip
                                            label="Removed"
                                            size="small"
                                            sx={{
                                                backgroundColor: '#FED7D7',
                                                color: '#C53030',
                                                fontSize: '0.7rem',
                                                height: '20px',
                                                fontWeight: 600,
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* Timestamp */}
                                <Tooltip title={formatAbsoluteTime(historyItem.timestamp)} arrow>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#718096',
                                            display: 'block',
                                        }}
                                    >
                                        {formatRelativeTime(historyItem.timestamp)}
                                    </Typography>
                                </Tooltip>

                                {/* Expand/Collapse for details */}
                                {hasChanges && (
                                    <>
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                toggleExpanded(historyItem.revisionNumber)
                                            }
                                            endIcon={
                                                isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                                            }
                                            sx={{
                                                mt: 1,
                                                textTransform: 'none',
                                                color: '#4A5568',
                                                '&:hover': {
                                                    backgroundColor: '#F7FAFC',
                                                },
                                            }}
                                        >
                                            {isExpanded ? 'Hide' : 'Show'} Details
                                        </Button>

                                        <Collapse in={isExpanded}>
                                            {renderChangeDetails(historyItem)}
                                        </Collapse>
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 2,
                    px: 3,
                    pt: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon sx={{ color: '#1652F0' }} />
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            color: '#1A202C',
                            fontSize: '1.25rem',
                        }}
                    >
                        Document History
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        color: '#718096',
                        '&:hover': {
                            backgroundColor: '#F7FAFC',
                            color: '#4A5568',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 3, pt: 2 }}>
                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 300,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && history.length === 0 && (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 8,
                        }}
                    >
                        <HistoryIcon
                            sx={{
                                fontSize: 64,
                                color: '#CBD5E0',
                                mb: 2,
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#4A5568',
                                fontWeight: 600,
                                mb: 1,
                            }}
                        >
                            No History Yet
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#718096',
                            }}
                        >
                            Document and folder activity will appear here
                        </Typography>
                    </Box>
                )}

                {!loading && !error && history.length > 0 && (
                    <Box>{history.map((historyItem) => renderHistoryItem(historyItem))}</Box>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: '#E2E8F0',
                        color: '#4A5568',
                        '&:hover': {
                            borderColor: '#CBD5E0',
                            backgroundColor: '#F7FAFC',
                        },
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CloseoutDocsHistory;
